import { DirectionalLight, Float32BufferAttribute, HemisphereLight, Mesh, MeshStandardMaterial, Vector2 } from 'three';
import { Robot } from './objects/Robot';
import { Vector3 } from 'three';
import { create2dArray } from '../utils/create-2d-array';
import { GeometryBuilder } from '../utils/geometry-builder';
import { Goal } from './objects/Goal';
import { LevelObject } from './LevelObject';
import '../stores/current-level';
/**
 * LevelMap - Represents the area the current level is being played in, manages creation and
 * destruction of map elements, tracks obstacles at specific locations. Must exist inside a
 * World object.
 */
export class LevelMap {
    constructor(world, spec) {
        this.world = world;
        this.spec = spec;
        this.width = spec.width;
        this.height = spec.height;
        this.reservations = create2dArray(this.width, this.height, null);
        this.looseObjects = create2dArray(this.width, this.height, null);
        this.goals = [];
        this.setupLights();
        this.world.add(this.buildFloor(spec));
        this.spawnEntities(spec);
    }
    buildFloor(spec) {
        const builder = new GeometryBuilder([
            ['position', 3, Float32BufferAttribute],
            ['normal', 3, Float32BufferAttribute],
            ['color', 3, Float32BufferAttribute],
        ]);
        const white = [0.6, 0.7, 0.8];
        const blue = [0.1, 0.2, 0.8];
        for (let x = 0; x < this.width; x++) {
            for (let y = 0; y < this.height; y++) {
                if (spec.hasFloor(x, y)) {
                    const tileColor = (x + y) % 2 ? white : blue;
                    const centre = this.tileToWorld(new Vector2(x, y));
                    builder.addQuad([
                        [[centre.x - 0.5, 0, centre.z - 0.5], [0, 1, 0], tileColor],
                        [[centre.x - 0.5, 0, centre.z + 0.5], [0, 1, 0], tileColor],
                        [[centre.x + 0.5, 0, centre.z + 0.5], [0, 1, 0], tileColor],
                        [[centre.x + 0.5, 0, centre.z - 0.5], [0, 1, 0], tileColor],
                    ]);
                }
            }
        }
        return new LevelObject(this, new Mesh(builder.bake(), new MeshStandardMaterial({
            vertexColors: true,
        })), new Vector3(0, 0, 0));
    }
    spawnEntities(spec) {
        for (let x = 0; x < this.width; x++) {
            for (let y = 0; y < this.height; y++) {
                const spawner = spec.getSpawner(x, y);
                for (const obj of spawner(this, this.tileToWorld(new Vector2(x, y)))) {
                    this.world.add(obj);
                    if (obj instanceof Robot) {
                        this.reservations[x][y] = obj;
                    }
                    if (obj instanceof Goal) {
                        this.goals.push(new Vector2(x, y));
                    }
                }
            }
        }
    }
    setupLights() {
        var hemiLight = new HemisphereLight(0x777777, 0);
        hemiLight.position.set(0, 100, 0);
        this.world.scene.add(hemiLight);
        var dirLight = new DirectionalLight(0xffffff);
        dirLight.position.set(25, 25, 25);
        this.world.scene.add(dirLight);
    }
    /**
     * Get a list of objects at the specified tile, including obstacles and other floating objects.
     * @TODO Add support for non-obstacle objects.
     * @param {Vector2} tile - Position to fetch objects from.
     */
    getObjectsAt(tile) {
        const objects = [];
        if (this.inBounds(tile)) {
            const obstacle = this.reservations[tile.x][tile.y];
            if (obstacle) {
                objects.push(obstacle);
            }
            const loose = this.looseObjects[tile.x][tile.y];
            if (loose) {
                objects.push(loose);
            }
        }
        return objects;
    }
    tileToWorld(tile, height = 0) {
        return new Vector3(tile.x - (this.width / 2) + 0.5, height, tile.y - (this.height / 2) + 0.5);
    }
    worldToTile(world) {
        return new Vector2(Math.floor(world.x + (this.width / 2)), Math.floor(world.z + (this.height / 2)));
    }
    inBounds(position) {
        if (position.x < 0 || position.x >= this.width || position.y < 0 || position.y >= this.height) {
            return false;
        }
        return this.spec.getTile(position.x, position.y) !== ' ';
    }
    isGoal(position) {
        return !!this.goals.find(x => position.equals(x));
    }
    reserveSpace(position, obj) {
        if (!this.inBounds(position)) {
            return false;
        }
        if (this.reservations[position.x][position.y] !== null) {
            return false;
        }
        this.reservations[position.x][position.y] = obj;
        return true;
    }
    releaseSpace(position, obj) {
        if (this.reservations[position.x][position.y] === obj) {
            this.reservations[position.x][position.y] = null;
        }
    }
    getObstacleAt(position) {
        return this.reservations[position.x][position.y];
    }
    placeLooseObject(target, obj) {
        if (!this.inBounds(target)) {
            return false;
        }
        if (this.looseObjects[target.x][target.y] !== null) {
            // TODO: Look at combining loose objects.
            return false;
        }
        this.looseObjects[target.x][target.y] = obj;
        // Figure out the height the object will drop at.
        const obstacle = this.getObstacleAt(target);
        const height = obstacle ? obstacle.shelfHeight : 0;
        obj.position = this.tileToWorld(target, height);
        return true;
    }
    removeLooseObject(object) {
        const at = this.worldToTile(object.position);
        if (this.looseObjects[at.x][at.y] === object) {
            this.looseObjects[at.x][at.y] = null;
        }
    }
    getLooseObject(at) {
        if (!this.inBounds(at)) {
            return null;
        }
        return this.looseObjects[at.x][at.y];
    }
    /**
     * Return this level map expressed as a JSON object
     */
    toApiJson() {
        return {
            width: this.width,
            height: this.height,
            cells: create2dArray(this.width, this.height, (x, y) => this.getObjectsAt(new Vector2(x, y)).map(x => x.getProperties())),
        };
    }
}
